import React, { MouseEvent, useCallback, useMemo } from 'react';
import { GenericCard } from 'components/display/Card/Card';
import { Button } from 'components/ui/Button';
import { ContractSignatureRequest } from '../signature/ContractSignatureRequest';
import { Sizes } from 'enums/sizes';
import { usePanel } from 'hooks/usePanel';
import { getContractStatusLabel } from '../../utils/getContractCardInfos';
import GenericBadge from 'components/ui/Badge';
import { NsKey } from 'enums/NsKey';
import { useTranslation } from 'react-i18next';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { TitleIconKey } from 'enums/titleIconKey';
import {
  useQuoteContractualizationData,
  useContractStatus,
  useContractBadgeColor,
  useIsSendContractDisabled,
  useCanSignContract
} from '../../store';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';

/**
 * Raisons que le back pose sur `disabledReasons.canSendContract` quand l'envoi est
 * refusé. La carte les affiche telles quelles : sans elles, le bouton disparaissait
 * sans dire pourquoi. « Contrat déjà signé » n'y figure pas — le badge le dit déjà.
 */
const SEND_CONTRACT_REASONS = [
  'INSURED_INCOMPLETE',
  'RISK_INCOMPLETE',
  'INSURED_OR_RISK_INCOMPLETE',
  'SEPA_MANDATE_MISSING',
  'MANDATE_NOT_SIGNED',
  // Quote validation (tunnel requiring a validator's approval)
  'VALIDATION_REQUIRED',
  'VALIDATION_PENDING',
  'VALIDATION_REFUSED',
  'VALIDATION_CONFIG_INCOMPLETE'
] as const;

export const SignContractCard: React.FC = () => {
  const { openPanel } = usePanel();
  const { t } = useTranslation(NsKey.Contractualization);

  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();

  const quoteData = useQuoteContractualizationData();

  // Utilisation des selecteurs centralises
  const currentStatus = useContractStatus();
  const badgeColor = useContractBadgeColor();
  const isSendContractDisabled = useIsSendContractDisabled();
  const canSignContract = useCanSignContract();

  // Le bouton est desactive si ni canSendContract ni canSignContract ne sont true
  const isButtonDisabled = isSendContractDisabled && !canSignContract;

  // Raison affichee a la place du bouton : seules les cles connues sont traduites.
  const disabledReason = useMemo(() => {
    const reason = quoteData?.disabledReasons?.canSendContract;
    if (!isButtonDisabled || !reason) return null;
    return (SEND_CONTRACT_REASONS as readonly string[]).includes(reason) ? reason : null;
  }, [isButtonDisabled, quoteData?.disabledReasons?.canSendContract]);

  const onSelectContractSignatureRequest = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!quoteData) return;
      openPanel(
        {
          title: t('CONTRACTUALIZATION.CARDS.SEND_CONTRACT_TO_SIGN'),
          size: Sizes.xl
        },
        <ContractSignatureRequest onSuccess={refreshSubscriptionProcess} />
      );
    },
    [openPanel, t, quoteData, refreshSubscriptionProcess]
  );

  return (
    <GenericCard className="w-full">
      <GenericCard.Header
        disabled={isButtonDisabled}
        icon={<TitleIconFactory titleIconKey={TitleIconKey.Handshake} />}
        titleAside={
          <GenericBadge
            color={badgeColor}
            label={getContractStatusLabel(currentStatus)}
            badgeType="status"
          />
        }
      >
        {t('CONTRACTUALIZATION.CARDS.SIGN_CONTRACT_TITLE')}
      </GenericCard.Header>
      {isButtonDisabled && disabledReason && (
        <GenericCard.Body>
          <p className="text-center text-sm text-gray-600">
            {t(`CONTRACTUALIZATION.CARDS.SEND_CONTRACT_DISABLED.${disabledReason}`)}
          </p>
        </GenericCard.Body>
      )}
      {!isButtonDisabled && (
        <GenericCard.Body>
          <div className="text-center">
            <Button
              color="secondary"
              round
              onClick={onSelectContractSignatureRequest}
            >
              {canSignContract
                ? t('CONTRACTUALIZATION.CARDS.SIGN_BUTTON_REMINDER')
                : t('CONTRACTUALIZATION.CARDS.SIGN_BUTTON')}
            </Button>
          </div>
        </GenericCard.Body>
      )}
    </GenericCard>
  );
};
